import React from "react";
import Layout from "../../components/Layout";
import OpenGraphTitle from "../../components/OpenGraphTitle";
import OpenGraphDescription from "../../components/OpenGraphDescription";
import Head from "../../components/head";
import { ServicesPageProvider } from "../../context/ServicesPageContext";
import FranchiseeContext, { FranchiseeProvider } from "../../context/Franchisee";
import ServiceTitle from "../../components/ServiceTitle";
import ServicesDescription from "../../components/ServicesDescription";
import { Col, Row } from "react-bootstrap";
import Link from "next/link";
import ContactUs from "../../components/ContactUs";

interface ApartmentCleanPageProps {
  /* add services page props here */
}

const ApartmentClean: React.FC<ApartmentCleanPageProps> = () => (
  <FranchiseeProvider>
    <ServicesPageProvider>
      <Layout alternateFooter={true}>
        <Head
          slug="apartment-clean"
          title="Apartment Cleaning Services - Two Maids"
          description="Your apartment requires special attention to protect your deposit. Our apartment cleaning services are tailored with care to accomplish just that."
        />
        <OpenGraphTitle title="Apartment Cleaning Services - Two Maids" />

        <OpenGraphDescription>
          Your apartment requires special attention to protect your deposit. Our apartment cleaning services are tailored with care to accomplish just that.
        </OpenGraphDescription>

        <ServiceTitle imageSrc="https://res.cloudinary.com/twomaidsengi/image/upload/v1724955996/hubsite-reskin/New%20Services%20Pages/apartment-cleaning.webp">Apartment Cleaning</ServiceTitle>
        <div className="service-description gray-background">
          <div className="pb-3">
            <h2>Apartment Cleaning Services</h2>
            <p>
              Living in an apartment, condominium, or townhome can be
              particularly challenging, as these spaces can quickly become
              cluttered and chaotic, especially if you have kids or pets. The
              mess is often unavoidable, with limited storage and high-traffic
              areas constantly in use. Two Maids understands these unique
              challenges and the nuances of apartment living, offering
              specialized cleaning services tailored to meet your specific needs,
              ensuring your space remains pristine and stress-free.
            </p>
            <p>
              There are also specific considerations apartment cleaning, and Two Maids
              cleaning service can accomodate these potential considerations
            </p>
            <ul>
              <li>
                <b className="gotham-bold">Limited Space:</b> Apartments often have limited space, which can make maneuvering and accessing
                certain areas more difficult. This includes tight corners, small closets, and cramped bathrooms.
              </li>
              <li>
                <b className="gotham-bold">Noise Concerns:</b> Cleaning in an apartment building may require consideration for noise levels,
                especially if neighbors are nearby. Using loud vacuum cleaners or cleaning at certain times of the day
                could disturb others.
              </li>
              <li>
                <b className="gotham-bold">Building Regulations:</b> Some apartment buildings may have specific cleaning regulations or restrictions
                imposed by the management or homeowners' association. This could include rules about cleaning products,
                disposal of trash, or scheduling of cleaning services.
              </li>
              <li>
                <b className="gotham-bold">Elevator Dependency:</b> If the apartment is located in a high-rise building, transporting cleaning
                supplies and equipment via elevators can be time-consuming and may require coordination with other residents.
              </li>
            </ul>
          </div>
          <p>
            From the differences in size and prioritizing tasks, to restrictions due to specific regulations and rules,
            and even security, you need a cleaning service that can not only do a great job keeping your space clean,
            but also make sure that we are doing everything you can to adhere to your renter’s agreement.
          </p>
          <p>
            When inviting Two Maids into your apartment, you are trusting a cleaning company that wants your space to
            be cleaner than it was when you first moved in.
          </p>
        </div>
        <ServicesDescription colSize={12}>
          <h3 className="service-description-title">What You Can Expect From Our Apartment Cleaning Services</h3>
          <p>
            Our cleaning professionals are trained to follow a detailed checklist that covers every nook and cranny
            of your apartment, ensuring nothing is overlooked. This checklist includes but is not limited to:
          </p>
          <ul>
            <li>
              <b className="gotham-bold">Kitchen:</b> We clean the inside and outside of microwaves, polish the exteriors of
              refrigerators and other appliances, sweep and mop floors, and clean countertops, the outsides of
              stove hoods, backsplashes, trash can exteriors, and more.
            </li>
            <li>
              <b className="gotham-bold">Bathroom Sanitation:</b> We clean your showers and bathtubs, polish faucets, mirrors, and toilets, 
              and dust accessible surfaces to provide a fresh bathroom
            </li>
            <li>
              <b className="gotham-bold">Living Areas and Bedrooms:</b> Expect dust-free furniture, cleaned mirrors, and polished glass
              surfaces. We empty trash, replace liners, make beds, change linens, and even handle the high ceiling
              fans, light fixtures, and cobwebs. Windows, windowsills, blinds, doors, doorframes, light switches,
              and outlet covers are all included in our cleaning regime.
            </li>
            <li>
              <b className="gotham-bold">Special Attention Areas:</b> We know firsthand the importance of caring for an apartment, so we're
              extra careful with hardwood floors, carpets, and delicate surfaces.
            </li>
          </ul>
        </ServicesDescription>
        <div className="service-description promise-description">
          <img className="service-description-section-promise-svg mr-3" src="https://res.cloudinary.com/twomaidsengi/image/upload/v1724955994/hubsite-reskin/New%20Services%20Pages/promise.svg" />
          <div>
            <h3 className="service-description-title">What if Something Goes Wrong?</h3>
            <p>
              Treating our clients' homes like our own, we don't take the invitation into your space lightly. If something
              unexpected happens, we repair or replace any damage we cause. All our PHCs are fully licensed, bonded, and
              insured to protect you against any worst-case scenarios.
            </p>
            <p>
              We treat your space with the utmost respect, and if your apartment has specific requirements (or you have
              personal preferences), such as all-natural green cleaning, we are always happy to use your own cleaning supplies.
            </p>
            <p>
              If you'd rather us use what you have on hand, you can leave the product on your kitchen counter and let our office
              know ahead of time about your preferences. Products must have a store-bought label with warnings and ingredients.
              While our cleaning company doesn't offer a discount for using customer-provided supplies, we're more than happy to
              use them in our house cleaning.
            </p>
          </div>
        </div>
        <div className="service-description promise-description pt-0">
          <div className="service-description-ready-check">
            <img className="service-description-section-img" src="https://res.cloudinary.com/twomaidsengi/image/upload/v1724955992/hubsite-reskin/New%20Services%20Pages/ready-checks.svg" />
          </div>
          <div>
            <h3 className="service-description-title">Ready for Your Apartment Cleaning?</h3>
            <p>
              Choosing Two Maids' cleaning team for your apartment's residential cleaning service means investing in a cleaner,
              healthier living space without the hassle of worrying about damage. Let us take the stress of cleaning off your
              shoulders, so you can enjoy your time at your apartment to the fullest, from the day you move in to the day you leave.
            </p>
            <FranchiseeContext.Consumer>
              {({ franchisee }) => (
                <p>
                  <a href={
                    franchisee?.id > -1
                      ? `/${franchisee?.slug}/contact/`
                      : '/'
                  }>Contact us</a> today to discuss what housekeeping services can best benefit you and your space,
                  so we can make your apartment shine.
                </p>
              )}
            </FranchiseeContext.Consumer>

            <a href="/services/move-in-move-out"><h3 className="service-description-title mt-3">Moving Out, Check out our Move Out Package</h3></a>
          </div>
        </div>
        <ContactUs
          title="Ready for some Help?"
          buttons={
            <Row className="d-flex justify-content-start">
              <Col md={"auto"} className="mb-3">
                <Link href={process.env.SALES_FUNNEL_URL}>
                  <a className="contact-us-button">BOOK YOUR CLEANING</a>
                </Link>
              </Col>
              <Col md={"auto"}>
                <Link href={process.env.SALES_FUNNEL_URL}>
                  <a className="contact-us-button">CALCULATE YOUR PRICE</a>
                </Link>
              </Col>
            </Row>
          }
        >
          <p>
            Whether you're overwhelmed by daily responsibilities, struggling to
            find time for yourself, or simply in need of a deep refresh, our
            apartment cleaning service is the first step toward reclaiming
            your space. Let Two Maids help you transition into a new season of
            life: sparkly, clean, and with a clear mind.
          </p>
        </ContactUs>
      </Layout>
    </ServicesPageProvider>
  </FranchiseeProvider>
);
export default ApartmentClean;
